import axios from 'axios';

const API_BASE_URL = import.meta.env.REACT_APP_WORDPRESS_URL;
const CONSUMER_KEY = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY;
const CONSUMER_SECRET = import.meta.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET;

// Create axios instance for WooCommerce API
const wooAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/wc/v3`,
  auth: {
    username: CONSUMER_KEY,
    password: CONSUMER_SECRET,
  },
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for WooCommerce Store API (for cart operations)
const storeAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/wc/store/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for WordPress comments (fallback for reviews)
const wpAPI = axios.create({
  baseURL: `${API_BASE_URL}/wp-json/wp/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const productService = {
  async getProducts(params = {}) {
    try {
      const queryParams = {
        per_page: params.per_page || 20,
        page: params.page || 1,
        status: 'publish',
        type: 'simple',
        ...params,
      };

      const response = await wooAPI.get('/products', { params: queryParams });
      
      return {
        products: response.data,
        totalPages: parseInt(response.headers['x-wp-totalpages']) || 1,
        total: parseInt(response.headers['x-wp-total']) || 0,
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('Failed to fetch products');
    }
  },

  async getFeaturedProducts(limit = 8) {
    try {
      const response = await wooAPI.get('/products', {
        params: {
          featured: true,
          per_page: limit,
          status: 'publish',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw new Error('Failed to fetch featured products');
    }
  },

  async getProductById(id) {
    try {
      const response = await wooAPI.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error('Product not found');
    }
  },

  async getProductBySlug(slug) {
    try {
      const response = await wooAPI.get('/products', {
        params: { slug },
      });

      if (response.data.length === 0) {
        throw new Error('Product not found');
      }

      return response.data[0];
    } catch (error) {
      console.error('Error fetching product by slug:', error);
      throw new Error('Product not found');
    }
  },

  async searchProducts(query, params = {}) {
    try {
      const searchParams = {
        search: query,
        per_page: params.per_page || 20,
        page: params.page || 1,
        status: 'publish',
        ...params,
      };

      const response = await wooAPI.get('/products', { params: searchParams });
      
      return {
        products: response.data,
        totalPages: parseInt(response.headers['x-wp-totalpages']) || 1,
        total: parseInt(response.headers['x-wp-total']) || 0,
      };
    } catch (error) {
      console.error('Error searching products:', error);
      throw new Error('Search failed');
    }
  },

  async getProductCategories() {
    try {
      const response = await wooAPI.get('/products/categories', {
        params: {
          per_page: 100,
          hide_empty: true,
          orderby: 'count',
          order: 'desc',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories');
    }
  },

  async getProductsByCategory(categoryId, params = {}) {
    try {
      const queryParams = {
        category: categoryId,
        per_page: params.per_page || 20,
        page: params.page || 1,
        status: 'publish',
        ...params,
      };

      const response = await wooAPI.get('/products', { params: queryParams });
      
      return {
        products: response.data,
        totalPages: parseInt(response.headers['x-wp-totalpages']) || 1,
        total: parseInt(response.headers['x-wp-total']) || 0,
      };
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw new Error('Failed to fetch products');
    }
  },

  // Enhanced Review functionality with multiple fallback strategies
  async getProductReviews(productId) {
    try {
      // First, check if reviews are enabled
      const reviewsEnabled = await this.checkReviewsEnabled();
      if (!reviewsEnabled) {
        console.info('Reviews are disabled on this WooCommerce store');
        return this.getMockReviews(productId);
      }

      // Try the product-specific reviews endpoint first
      const response = await wooAPI.get(`/products/${productId}/reviews`, {
        params: {
          per_page: 100,
          status: 'approved',
        },
      });

      return response.data;
    } catch (error) {
      console.warn('Product-specific reviews endpoint failed:', error.message);
      
      // Check if it's a "PRO FEATURE ONLY" error
      if (error.response?.data === 'PRO FEATURE ONLY' || error.response?.status === 404) {
        console.info('WooCommerce reviews endpoint not available (may require pro plugin)');
        return await this.getReviewsViaWordPressComments(productId);
      }
      
      try {
        // Fallback to general reviews endpoint with product filter
        const response = await wooAPI.get('/products/reviews', {
          params: {
            product: [productId],
            per_page: 100,
            status: 'approved',
          },
        });

        return response.data;
      } catch (fallbackError) {
        console.warn('General reviews endpoint also failed:', fallbackError.message);
        
        // Try WordPress comments as final fallback
        return await this.getReviewsViaWordPressComments(productId);
      }
    }
  },

  // Fallback method to get reviews via WordPress comments API
  async getReviewsViaWordPressComments(productId) {
    try {
      const response = await wpAPI.get('/comments', {
        params: {
          post: productId,
          status: 'approve',
          per_page: 100,
        },
      });

      // Transform WordPress comments to review format
      return response.data.map(comment => ({
        id: comment.id,
        product_id: productId,
        reviewer: comment.author_name,
        reviewer_email: comment.author_email,
        review: comment.content.rendered.replace(/<[^>]*>/g, ''), // Strip HTML
        rating: this.extractRatingFromComment(comment),
        date_created: comment.date,
        verified: false, // Can't determine from comments API
        status: 'approved'
      }));
    } catch (error) {
      console.warn('WordPress comments fallback also failed:', error.message);
      
      // Return mock data for development or empty array for production
      if (import.meta.env.NODE_ENV === 'development') {
        console.info('Using mock reviews for development');
        return this.getMockReviews(productId);
      }
      
      return [];
    }
  },

  // Extract rating from comment meta (if available)
  extractRatingFromComment(comment) {
    // This would need to be customized based on how your WooCommerce stores ratings
    // Usually stored in comment meta, but not accessible via REST API without custom endpoint
    // Return a default rating for now
    return Math.floor(Math.random() * 5) + 1; // Random rating 1-5 for demo
  },

  // Mock reviews for development/testing
  getMockReviews(productId) {
    return [
      {
        id: 1,
        product_id: productId,
        reviewer: 'John Doe',
        reviewer_email: 'john@example.com',
        review: 'This is an amazing product! Really high quality and exactly what I needed for my project.',
        rating: 5,
        date_created: '2024-01-15T10:30:00',
        verified: true,
        status: 'approved'
      },
      {
        id: 2,
        product_id: productId,
        reviewer: 'Sarah Smith',
        reviewer_email: 'sarah@example.com',
        review: 'Good product overall, but could use some improvements in the documentation.',
        rating: 4,
        date_created: '2024-01-10T14:20:00',
        verified: true,
        status: 'approved'
      },
      {
        id: 3,
        product_id: productId,
        reviewer: 'Mike Johnson',
        reviewer_email: 'mike@example.com',
        review: 'Decent quality but took longer to download than expected. Support was helpful though.',
        rating: 3,
        date_created: '2024-01-05T09:15:00',
        verified: false,
        status: 'approved'
      }
    ];
  },

 async createProductReview(reviewData) {
    try {
      // Validate review data first
      const validationErrors = this.validateReviewData(reviewData);
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
      }

      // Always use WordPress comments API for review creation
      return await this.createReviewViaWordPressComments(reviewData);
    } catch (error) {
      console.error('Failed to create review:', error.message);

      // If in dev, simulate success
      if (import.meta.env.NODE_ENV === 'development') {
        return {
          id: Date.now(),
          product_id: reviewData.product_id,
          review: reviewData.review,
          reviewer: reviewData.reviewer,
          reviewer_email: reviewData.reviewer_email,
          rating: reviewData.rating,
          date_created: new Date().toISOString(),
          status: 'pending'
        };
      }

      throw new Error('Failed to submit review. Please try again later.');
    }
  },

  // ✅ WordPress Comments API method for review creation
  async createReviewViaWordPressComments(reviewData) {
    try {
      const response = await wpAPI.post('/comments', {
        post: reviewData.product_id, // Product = WP post
        author_name: reviewData.reviewer,
        author_email: reviewData.reviewer_email,
        content: reviewData.review,
        meta: {
          rating: reviewData.rating // Store star rating in comment meta
        }
      });

      return {
        id: response.data.id,
        product_id: reviewData.product_id,
        review: reviewData.review,
        reviewer: reviewData.reviewer,
        reviewer_email: reviewData.reviewer_email,
        rating: reviewData.rating,
        date_created: response.data.date,
        status: 'pending'
      };
    } catch (error) {
      console.error('WordPress comments API failed:', error.response?.data || error.message);
      throw new Error('Could not submit review. Please try again later.');
    }
  },

  // Check if reviews are enabled for the store
  async checkReviewsEnabled() {
    try {
      const response = await wooAPI.get('/settings/products/woocommerce_enable_reviews');
      return response.data.value === 'yes';
    } catch (error) {
      console.warn('Could not check if reviews are enabled:', error.message);
      
      // Try alternative method to check if reviews endpoint exists
      try {
        await wooAPI.get('/products/reviews', { params: { per_page: 1 } });
        return true;
      } catch (reviewsError) {
        if (reviewsError.response?.data === 'PRO FEATURE ONLY' || reviewsError.response?.status === 404) {
          return false;
        }
        return true; // Assume enabled if we can't determine
      }
    }
  },

  // Check what review capabilities are available
  async getReviewCapabilities() {
    const capabilities = {
      canRead: false,
      canWrite: false,
      method: 'none',
      message: ''
    };

    try {
      // Test WooCommerce reviews endpoint
      await wooAPI.get('/products/reviews', { params: { per_page: 1 } });
      capabilities.canRead = true;
      capabilities.canWrite = true;
      capabilities.method = 'woocommerce';
      capabilities.message = 'Full WooCommerce reviews support available';
      return capabilities;
    } catch (error) {
      if (error.response?.data === 'PRO FEATURE ONLY') {
        capabilities.message = 'WooCommerce reviews requires pro plugin';
      }
    }

    try {
      // Test WordPress comments endpoint
      await wpAPI.get('/comments', { params: { per_page: 1, type: 'review' } });
      capabilities.canRead = true;
      capabilities.canWrite = true;
      capabilities.method = 'wordpress_comments';
      capabilities.message = capabilities.message || 'Using WordPress comments for reviews';
      return capabilities;
    } catch (error) {
      capabilities.message = capabilities.message || 'WordPress comments not accessible';
    }

    // If neither works, we can only provide mock data
    if (import.meta.env.NODE_ENV === 'development') {
      capabilities.canRead = true;
      capabilities.canWrite = true;
      capabilities.method = 'mock';
      capabilities.message = 'Using mock data for development';
    } else {
      capabilities.message = 'Reviews not available on this store';
    }

    return capabilities;
  },

  async getProductAttributes() {
    try {
      const response = await wooAPI.get('/products/attributes');
      return response.data;
    } catch (error) {
      console.error('Error fetching attributes:', error);
      return [];
    }
  },

  async getProductTags() {
    try {
      const response = await wooAPI.get('/products/tags', {
        params: {
          per_page: 100,
          hide_empty: true,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  },

  // Store API methods for cart operations
  async getStoreProducts(params = {}) {
    try {
      const response = await storeAPI.get('/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching store products:', error);
      throw new Error('Failed to fetch products');
    }
  },

  async getStoreProduct(id) {
    try {
      const response = await storeAPI.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching store product:', error);
      throw new Error('Product not found');
    }
  },

  // Helper methods
  formatPrice(price, currency = '₹') {
    return `${currency}${parseFloat(price).toFixed(2)}`;
  },

  getProductImage(product) {
    if (product.images && product.images.length > 0) {
      return product.images[0].src;
    }
    return '/placeholder-product.jpg';
  },

  isProductInStock(product) {
    return product.stock_status === 'instock';
  },

  getProductStock(product) {
    if (product.manage_stock) {
      return product.stock_quantity;
    }
    return product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock';
  },

  calculateDiscountPercentage(regularPrice, salePrice) {
    if (!regularPrice || !salePrice || regularPrice <= salePrice) return 0;
    return Math.round(((regularPrice - salePrice) / regularPrice) * 100);
  },

  getProductRating(product) {
    return {
      average: parseFloat(product.average_rating) || 0,
      count: parseInt(product.rating_count) || 0,
    };
  },

  formatProductForCart(product) {
    return {
      id: product.id,
      name: product.name,
      price: parseFloat(product.price),
      image: this.getProductImage(product),
      slug: product.slug,
      sku: product.sku,
      stock_status: product.stock_status,
    };
  },

  // Calculate average rating from reviews array
  calculateAverageRating(reviews) {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + (review.rating || 0), 0);
    return sum / reviews.length;
  },

  // Validate review data before submission
  validateReviewData(reviewData) {
    const errors = [];

    if (!reviewData.product_id) {
      errors.push('Product ID is required');
    }

    if (!reviewData.review || reviewData.review.trim().length < 10) {
      errors.push('Review must be at least 10 characters long');
    }

    if (!reviewData.rating || reviewData.rating < 1 || reviewData.rating > 5) {
      errors.push('Rating must be between 1 and 5');
    }

    if (!reviewData.reviewer || reviewData.reviewer.trim().length < 2) {
      errors.push('Reviewer name must be at least 2 characters long');
    }

    if (!reviewData.reviewer_email || !/^\S+@\S+\.\S+$/.test(reviewData.reviewer_email)) {
      errors.push('Valid email address is required');
    }

    return errors;
  },

  // Advanced filtering
  async getProductsWithFilters(filters = {}) {
    try {
      const params = {
        per_page: filters.per_page || 20,
        page: filters.page || 1,
        status: 'publish',
      };

      // Add category filter
      if (filters.categories && filters.categories.length > 0) {
        params.category = filters.categories.join(',');
      }

      // Add price range filter
      if (filters.min_price) {
        params.min_price = filters.min_price;
      }
      if (filters.max_price) {
        params.max_price = filters.max_price;
      }

      // Add stock status filter
      if (filters.in_stock) {
        params.stock_status = 'instock';
      }

      // Add on sale filter
      if (filters.on_sale) {
        params.on_sale = true;
      }

      // Add attribute filters
      if (filters.attributes) {
        Object.entries(filters.attributes).forEach(([attribute, terms]) => {
          params[`attribute`] = attribute;
          params[`attribute_term`] = terms.join(',');
        });
      }

      // Add sorting
      if (filters.orderby) {
        params.orderby = filters.orderby;
        params.order = filters.order || 'desc';
      }

      const response = await wooAPI.get('/products', { params });
      
      return {
        products: response.data,
        totalPages: parseInt(response.headers['x-wp-totalpages']) || 1,
        total: parseInt(response.headers['x-wp-total']) || 0,
      };
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      throw new Error('Failed to fetch products');
    }
  },
};